import {
  Sparkles, Bell, AlertCircle, CheckCircle2, Loader2, Mail, Phone,
  ExternalLink as PopupIcon,
} from 'lucide-react';
import { GRAD, fg, fgMuted, border, surface, muted } from './profile-shared';
import { APP_VERSION_DISPLAY } from '@/lib/app-version';
import MfaCard from '@/components/auth/MfaCard';

type PushState = ReturnType<typeof import('@/lib/push/usePushNotifications').usePushNotifications>;

interface PermissionsTabProps {
  isAdmin: boolean;
  aiEnabled: boolean;
  setAiEnabled: (v: boolean) => void;
  push: PushState;
  notifPrefs: { email: boolean; sms: boolean; push: boolean; unreadDigest: boolean };
  notifSaving: 'email' | 'sms' | 'unreadDigest' | null;
  saveNotifPref: (patch: Partial<{ email: boolean; sms: boolean; unreadDigest: boolean }>) => void;
  newTab: boolean;
  setNewTab: (v: boolean) => void;
  popupResult: 'ok' | 'blocked' | null;
  setPopupResult: (v: 'ok' | 'blocked' | null) => void;
  onTestPopup: () => void;
  phone: string;
}

export default function PermissionsTab({
  isAdmin, aiEnabled, setAiEnabled, push, notifPrefs, notifSaving, saveNotifPref,
  newTab, setNewTab, popupResult, setPopupResult, onTestPopup, phone,
}: PermissionsTabProps) {
  return (
    <div className="flex flex-col gap-5">
      {/* Two-factor authentication */}
      <MfaCard isAdmin={isAdmin} />

      {/* AI Assistant preference */}
      <div className="rounded-2xl p-5" style={{ background: surface, border: `1px solid ${border}` }}>
        <h2 className="mb-4 text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif', color: fgMuted }}>
          AI Assistant
        </h2>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: `linear-gradient(135deg, ${GRAD}, #a855f7)`, color: '#fff' }}>
              <Sparkles size={15} />
            </span>
            <div>
              <p className="text-sm font-semibold" style={{ color: fg, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Show the AI assistant
              </p>
              <p className="text-xs" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Turn off to hide the floating chat bubble everywhere on this device.
              </p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={aiEnabled}
            onClick={() => setAiEnabled(!aiEnabled)}
            className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors"
            style={{ background: aiEnabled ? GRAD : 'hsl(var(--muted))' }}>
            <span className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
              style={{ transform: aiEnabled ? 'translateX(22px)' : 'translateX(2px)' }} />
          </button>
        </div>
      </div>

      {/* Notifications permission */}
      <div className="rounded-2xl p-5" style={{ background: surface, border: `1px solid ${border}` }}>
        <h2 className="mb-4 text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif', color: fgMuted }}>
          Notifications
        </h2>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: `linear-gradient(135deg, ${GRAD}, #a855f7)`, color: '#fff' }}>
              <Bell size={15} />
            </span>
            <div>
              <p className="text-sm font-semibold" style={{ color: fg, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Push notifications
              </p>
              <p className="text-xs" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Get alerts about new messages, invoices and project updates on this device.
              </p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={push.enabled}
            disabled={push.busy || !push.supported || push.status === 'denied' || push.iosNeedsInstall}
            onClick={() => { push.enabled ? push.disable() : push.enable(); }}
            className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50"
            style={{ background: push.enabled ? GRAD : 'hsl(var(--muted))' }}>
            {push.busy ? (
              <Loader2 size={13} className="mx-auto animate-spin text-white" />
            ) : (
              <span className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
                style={{ transform: push.enabled ? 'translateX(22px)' : 'translateX(2px)' }} />
            )}
          </button>
        </div>

        {push.iosNeedsInstall && (
          <p className="mt-3 flex items-start gap-1.5 text-xs font-medium" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            <AlertCircle size={13} className="mt-0.5 shrink-0" /> Add this app to your Home Screen first, then enable notifications.
          </p>
        )}
        {!push.iosNeedsInstall && push.status === 'denied' && (
          <p className="mt-3 flex items-start gap-1.5 text-xs font-medium" style={{ color: 'hsl(0 70% 55%)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            <AlertCircle size={13} className="mt-0.5 shrink-0" /> Notifications are blocked — allow them for this site in your browser settings.
          </p>
        )}
        {!push.iosNeedsInstall && push.status !== 'denied' && !push.supported && (
          <p className="mt-3 flex items-start gap-1.5 text-xs font-medium" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            <AlertCircle size={13} className="mt-0.5 shrink-0" /> Push notifications aren't supported on this device or browser.
          </p>
        )}
        {push.enabled && (
          <p className="mt-3 flex items-start gap-1.5 text-xs font-semibold" style={{ color: 'hsl(143 60% 40%)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            <CheckCircle2 size={13} className="mt-0.5 shrink-0" /> Notifications are on for this device.
          </p>
        )}

        {/* Channel preferences: email + SMS */}
        <div className="mt-5 border-t pt-5" style={{ borderColor: border }}>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            How we reach you when you're offline
          </p>

          {/* Email */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: 'hsla(243,75%,59%,0.1)', color: GRAD }}>
                <Mail size={15} />
              </span>
              <div>
                <p className="text-sm font-semibold" style={{ color: fg, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Email alerts
                </p>
                <p className="text-xs" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  New messages, invoices and project updates.
                </p>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={notifPrefs.email}
              disabled={notifSaving === 'email'}
              onClick={() => saveNotifPref({ email: !notifPrefs.email })}
              className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50"
              style={{ background: notifPrefs.email ? GRAD : 'hsl(var(--muted))' }}>
              {notifSaving === 'email' ? (
                <Loader2 size={13} className="mx-auto animate-spin text-white" />
              ) : (
                <span className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
                  style={{ transform: notifPrefs.email ? 'translateX(22px)' : 'translateX(2px)' }} />
              )}
            </button>
          </div>

          {/* SMS */}
          <div className="mt-4 flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: 'hsla(243,75%,59%,0.1)', color: GRAD }}>
                <Phone size={15} />
              </span>
              <div>
                <p className="text-sm font-semibold" style={{ color: fg, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Text message (SMS) alerts
                </p>
                <p className="text-xs" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {phone.trim() ? 'Sent to the phone number on your profile.' : 'Add a phone number to your profile to enable.'}
                </p>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={notifPrefs.sms}
              disabled={notifSaving === 'sms'}
              onClick={() => saveNotifPref({ sms: !notifPrefs.sms })}
              className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50"
              style={{ background: notifPrefs.sms ? GRAD : 'hsl(var(--muted))' }}>
              {notifSaving === 'sms' ? (
                <Loader2 size={13} className="mx-auto animate-spin text-white" />
              ) : (
                <span className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
                  style={{ transform: notifPrefs.sms ? 'translateX(22px)' : 'translateX(2px)' }} />
              )}
            </button>
          </div>

          {/* Unread reminder emails (batched digest) */}
          <div className="mt-4 flex items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: 'hsla(243,75%,59%,0.1)', color: GRAD }}>
                <Bell size={15} />
              </span>
              <div>
                <p className="text-sm font-semibold" style={{ color: fg, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Unread reminder emails
                </p>
                <p className="text-xs" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  A single reminder if messages, invoices or documents stay unread for 30 minutes.
                </p>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={notifPrefs.unreadDigest}
              disabled={notifSaving === 'unreadDigest'}
              onClick={() => saveNotifPref({ unreadDigest: !notifPrefs.unreadDigest })}
              className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50"
              style={{ background: notifPrefs.unreadDigest ? GRAD : 'hsl(var(--muted))' }}>
              {notifSaving === 'unreadDigest' ? (
                <Loader2 size={13} className="mx-auto animate-spin text-white" />
              ) : (
                <span className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
                  style={{ transform: notifPrefs.unreadDigest ? 'translateX(22px)' : 'translateX(2px)' }} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Pop-ups & file viewing preference */}
      <div className="rounded-2xl p-5" style={{ background: surface, border: `1px solid ${border}` }}>
        <h2 className="mb-4 text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif', color: fgMuted }}>
          Pop-ups & File Viewing
        </h2>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: `linear-gradient(135deg, ${GRAD}, #a855f7)`, color: '#fff' }}>
              <PopupIcon size={15} />
            </span>
            <div>
              <p className="text-sm font-semibold" style={{ color: fg, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Open files in a new tab
              </p>
              <p className="text-xs" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Turn off if your browser keeps blocking the pop-up — files will then open in the current tab instead.
              </p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={newTab}
            onClick={() => { setNewTab(!newTab); setPopupResult(null); }}
            className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors"
            style={{ background: newTab ? GRAD : 'hsl(var(--muted))' }}>
            <span className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
              style={{ transform: newTab ? 'translateX(22px)' : 'translateX(2px)' }} />
          </button>
        </div>

        {newTab && (
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={onTestPopup}
              className="rounded-xl px-4 py-2 text-sm font-semibold transition-all hover:opacity-90"
              style={{ background: muted, border: `1px solid ${border}`, color: fg, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Test pop-ups
            </button>
            {popupResult === 'ok' && (
              <span className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: 'hsl(143 60% 40%)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                <CheckCircle2 size={13} /> Pop-ups are allowed.
              </span>
            )}
            {popupResult === 'blocked' && (
              <span className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: 'hsl(0 70% 55%)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                <AlertCircle size={13} /> Blocked — allow pop-ups for this site, or turn the toggle off.
              </span>
            )}
          </div>
        )}
      </div>

      {/* App version */}
      <p className="text-center text-xs" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
        App version {APP_VERSION_DISPLAY}
      </p>
    </div>
  );
}
